import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import LanguageSelector, { SupportedLanguage } from "@/components/multilingual/LanguageSelector";
import { 
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { defaultLanguage, LANGUAGE_COOKIE_NAME, detectBrowserLanguage } from "@/lib/i18n";
import { cancellationPolicyTranslations } from "@/lib/i18n/cancellationPolicy";
import { DOCUMENT_VERSIONS } from "@/lib/constants/documentVersions";

const formatVersionDate = (version: string, language: string): string => {
  const locale = language === 'de' ? 'de-DE' : language === 'fr' ? 'fr-FR' : language === 'es' ? 'es-ES' : 'en-US';
  try {
    return new Date(version).toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric' });
  } catch {
    return version;
  }
};

const CancellationPolicy = () => {
  const [language, setLanguage] = useState<SupportedLanguage>(defaultLanguage);

  useEffect(() => {
    const savedLanguage = localStorage.getItem(LANGUAGE_COOKIE_NAME);
    if (savedLanguage && ['en', 'de', 'fr', 'es'].includes(savedLanguage)) {
      setLanguage(savedLanguage as SupportedLanguage);
    } else {
      const browserLang = detectBrowserLanguage();
      setLanguage(browserLang);
      localStorage.setItem(LANGUAGE_COOKIE_NAME, browserLang);
    }
  }, []);
  
  const t = cancellationPolicyTranslations[language];
  
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <div className="flex-grow pt-24">
        <div className="max-w-4xl mx-auto px-4 pt-8 pb-16">
          <div className="flex flex-wrap items-center justify-between mb-6">
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/">Home</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage>{t.breadcrumb}</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
            
            <div className="mt-2 sm:mt-0">
              <LanguageSelector language={language} onChange={setLanguage} />
            </div>
          </div>
          
          <h1 className="text-3xl font-bold mb-6">{t.title}</h1>
          
          <div className="prose prose-slate max-w-none space-y-8">
            {/* Right of Withdrawal Section */}
            <section>
              <h2 className="text-xl font-semibold mb-4">{t.withdrawalRight.title}</h2>
              {t.withdrawalRight.paragraphs.map((paragraph, index) => (
                <p key={index} className="mb-4">{paragraph}</p>
              ))}
            </section>

            {/* How to Exercise */}
            <section>
              <h2 className="text-xl font-semibold mb-4">{t.howToExercise.title}</h2>
              <p className="mb-4">{t.howToExercise.description}</p>
              <div className="bg-muted/50 rounded-lg p-4 mb-4">
                <p className="font-medium">{t.controller.name}</p>
                <p className="text-sm text-muted-foreground whitespace-pre-line">
                  {t.controller.address}
                </p>
                <p className="text-sm mt-2">
                  Email:{" "}
                  <a
                    href={`mailto:${t.controller.email}`}
                    className="text-primary hover:underline"
                  >
                    {t.controller.email}
                  </a>
                </p>
              </div>
            </section>

            {/* Consequences of Withdrawal */}
            <section>
              <h2 className="text-xl font-semibold mb-4">{t.consequences.title}</h2>
              {t.consequences.paragraphs.map((paragraph, index) => (
                <p key={index} className="mb-4">{paragraph}</p>
              ))}
            </section>

            {/* Special Notice for Digital Content */}
            <section>
              <Card className="border-primary/20 bg-primary/5">
                <CardHeader>
                  <CardTitle className="text-lg">{t.digitalContentNotice.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p>{t.digitalContentNotice.content}</p>
                </CardContent>
              </Card>
            </section>

            {/* Sample Withdrawal Form */}
            <section>
              <h2 className="text-xl font-semibold mb-4">{t.withdrawalForm.title}</h2>
              <p className="mb-4 text-sm text-muted-foreground">
                {t.withdrawalForm.instruction}
              </p>
              <Card>
                <CardContent className="pt-6">
                  <div className="space-y-4 font-mono text-sm">
                    <p>
                      <span className="font-medium">{t.withdrawalForm.toLabel}:</span>{" "}
                      {t.controller.name}, {t.controller.address.replace(/\n/g, ", ")}
                    </p>
                    <p>
                      {t.withdrawalForm.declarationText}
                    </p>
                    <div className="border-b border-dashed pt-4" />
                    <p>
                      <span className="font-medium">{t.withdrawalForm.orderedOnLabel}:</span>{" "}
                      _______________________
                    </p>
                    <p>
                      <span className="font-medium">{t.withdrawalForm.consumerNameLabel}:</span>{" "}
                      _______________________
                    </p>
                    <p>
                      <span className="font-medium">{t.withdrawalForm.consumerAddressLabel}:</span>{" "}
                      _______________________
                    </p>
                    <p>
                      <span className="font-medium">{t.withdrawalForm.signatureLabel}:</span>{" "}
                      _______________________
                    </p>
                    <p>
                      <span className="font-medium">{t.withdrawalForm.dateLabel}:</span>{" "}
                      _______________________
                    </p>
                  </div>
                </CardContent>
              </Card>
            </section>
            
            <p className="text-sm text-muted-foreground mt-8">
              {t.lastUpdatedLabel}: {formatVersionDate(DOCUMENT_VERSIONS.cancellationPolicy, language)}
            </p>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default CancellationPolicy;
